import { driver, Config, DriveStep, Driver } from "driver.js";
import "driver.js/dist/driver.css";
import { translate } from "../translations";
import featureAnnouncementsApi from "../../Api/featureAnnouncements";
import { FeatureAnnouncementKey, titleTranslationKey, descriptionTranslationKey } from "./featureAnnouncementsRegistry";

export type FeatureAnnouncementStepConfig = {
    key: FeatureAnnouncementKey;
    element: string;
};

// Module-level queue: sibling components on the same page (e.g. a top bar and a table, both
// mounted as part of the same parent) each call showEligible() independently. Collecting calls
// made within the same tick into one queue, flushed on the next microtask, lets them all land in
// a single Driver.js sequence instead of each spawning its own disconnected instance.
let pendingUserId: number | null = null;
let pendingSteps: FeatureAnnouncementStepConfig[] = [];
let flushScheduled = false;

const MOBILE_DRAWER_TRANSITION_MS = 300;

function markSeen(userId: number, key: FeatureAnnouncementKey): void {
    featureAnnouncementsApi.dismissFeatureAnnouncement(userId, key).catch(() => {});
}

function isMobileSidebar(): boolean {
    return !window.matchMedia("(min-width: 1024px)").matches;
}

// The admin sidebar is an off-screen drawer on mobile - a step spotlighting something inside it
// (e.g. a nav link) needs the drawer opened first, same as useOnboardingTour.ts's own tour does.
function targetsCollapsedSidebar(selector: string): boolean {
    const sidebar = document.getElementById("admin-sidebar");
    const target = document.querySelector(selector);
    return !!sidebar && !!target && sidebar.contains(target) && isMobileSidebar() && !sidebar.classList.contains("is-open");
}

function flush(): void {
    flushScheduled = false;
    const userId = pendingUserId;
    const steps = pendingSteps;
    pendingUserId = null;
    pendingSteps = [];

    if (userId === null || steps.length === 0) {
        return;
    }

    featureAnnouncementsApi
        .getEligibleFeatureAnnouncements(userId)
        .then(({ eligibleKeys }) => {
            const eligibleSteps = steps.filter(step => eligibleKeys.includes(step.key));
            if (eligibleSteps.length === 0) {
                return;
            }

            let openedDrawerForAnnouncement = false;

            function openMobileDrawerIfNeeded(step: FeatureAnnouncementStepConfig, driverObj: Driver): void {
                if (!targetsCollapsedSidebar(step.element)) {
                    return;
                }

                document.getElementById("admin-sidebar-hamburger")?.click();
                openedDrawerForAnnouncement = true;
                setTimeout(() => driverObj.refresh(), MOBILE_DRAWER_TRANSITION_MS);
            }

            function closeMobileDrawerIfOpenedByAnnouncement(): void {
                if (!openedDrawerForAnnouncement) {
                    return;
                }

                document.getElementById("admin-sidebar-hamburger")?.click();
                openedDrawerForAnnouncement = false;
            }

            const driveSteps: DriveStep[] = eligibleSteps.map(step => ({
                element: step.element,
                popover: {
                    title: "✨ " + translate(titleTranslationKey(step.key)),
                    description: translate(descriptionTranslationKey(step.key)),
                },
                onHighlightStarted: (_element, _driveStep, { driver: driverObj }) => openMobileDrawerIfNeeded(step, driverObj),
                onDeselected: () => closeMobileDrawerIfOpenedByAnnouncement(),
            }));

            const driverObj = driver({
                showProgress: eligibleSteps.length > 1,
                allowClose: false,
                steps: driveSteps,
                nextBtnText: translate("common.next"),
                prevBtnText: translate("common.back"),
                doneBtnText: translate("common.done"),
                onDestroyed: (_element, _step, { state }) => {
                    closeMobileDrawerIfOpenedByAnnouncement();
                    const reachedSteps = eligibleSteps.slice(0, (state.activeIndex ?? -1) + 1);
                    reachedSteps.forEach(step => markSeen(userId, step.key));
                },
            } satisfies Config);

            driverObj.drive();
        })
        .catch(() => {});
}

export function useFeatureAnnouncement() {
    /**
     * Queues these steps for this user onto the page-wide pending batch, then - on the next
     * microtask - fetches eligibility once and drives every eligible step from every caller as a
     * single Driver.js sequence. No-op if none are eligible. Best-effort: a failed eligibility
     * fetch never blocks the page.
     */
    function showEligible(userId: number, steps: FeatureAnnouncementStepConfig[]): void {
        pendingUserId = userId;
        pendingSteps = pendingSteps.concat(steps);

        if (!flushScheduled) {
            flushScheduled = true;
            Promise.resolve().then(flush);
        }
    }

    return { showEligible };
}
